/**
 * Customização AgSUS de @plone/volto/components/theme/ContentMetadataTags.
 *
 * Diferenças em relação ao core (18.33.1):
 * 1. og:image também sai do preview_image_link — campo que as News Items
 *    deste portal usam; o core só olha opengraph_image e o campo configurado
 *    em contentMetadataTagsImageField ("image").
 * 2. Quando o conteúdo não tem imagem nenhuma, emite a marca AgSUS
 *    (1200×630) como og:image padrão, para a prévia de link compartilhado
 *    nunca sair vazia.
 */
import React, { useEffect } from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  toPublicURL,
  getBaseUrl,
  flattenToAppURL,
} from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import { getNavroot } from '@plone/volto/actions/navroot/navroot';
import config from '@plone/volto/registry';
import { useDispatch, useSelector } from 'react-redux';

import ogImagePadrao from '../../../../theme/og-agsus.png';

const ContentMetadataTags = (props) => {
  const {
    opengraph_title,
    opengraph_description,
    seo_title,
    seo_description,
    seo_canonical_url,
    seo_noindex,
    title,
    description,
  } = props.content;

  const dispatch = useDispatch();
  const pathname = useSelector((state) => state.router.location.pathname);
  const navroot = useSelector((state) => state.navroot?.data?.navroot);
  const site = useSelector((state) => state.site?.data);

  useEffect(() => {
    if (pathname && !hasApiExpander('navroot', getBaseUrl(pathname))) {
      dispatch(getNavroot(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  const getContentImageInfo = () => {
    const { contentMetadataTagsImageField } = config.settings;
    const image = props.content[contentMetadataTagsImageField];
    const { opengraph_image, preview_image_link } = props.content;

    const contentImageInfo = {
      contentHasImage: false,
      url: null,
      height: null,
      width: null,
    };

    if (opengraph_image?.scales?.large?.download) {
      contentImageInfo.contentHasImage = true;
      contentImageInfo.url = opengraph_image.scales.large.download;
      contentImageInfo.height = opengraph_image.scales.large.height;
      contentImageInfo.width = opengraph_image.scales.large.width;
    } else if (image?.scales?.large?.download) {
      contentImageInfo.contentHasImage = true;
      contentImageInfo.url = image.scales.large.download;
      contentImageInfo.height = image.scales.large.height;
      contentImageInfo.width = image.scales.large.width;
    } else {
      const campo = preview_image_link?.image_field || 'image';
      const escalas = preview_image_link?.image_scales?.[campo]?.[0];
      const escala = escalas?.scales?.great || escalas?.scales?.large;
      const download = escala?.download || escalas?.download;
      if (download) {
        contentImageInfo.contentHasImage = true;
        contentImageInfo.url = `${flattenToAppURL(
          preview_image_link['@id'],
        )}/${download}`;
        contentImageInfo.height = escala?.height || escalas?.height;
        contentImageInfo.width = escala?.width || escalas?.width;
      }
    }

    if (!contentImageInfo.contentHasImage) {
      contentImageInfo.contentHasImage = true;
      contentImageInfo.url = ogImagePadrao;
      contentImageInfo.width = 1200;
      contentImageInfo.height = 630;
    }

    return contentImageInfo;
  };

  const contentImageInfo = getContentImageInfo();

  const getTitle = () => {
    const includeSiteTitle =
      config?.settings?.siteTitleFormat?.includeSiteTitle || false;
    const titleAndSiteTitleSeparator =
      config?.settings?.titleAndSiteTitleSeparator || '-';
    const navRootTitle = navroot?.title;
    const siteRootTitle = site?.['plone.site_title'];
    const titlePart = navRootTitle || siteRootTitle;

    if (includeSiteTitle && titlePart && titlePart !== title) {
      return seo_title || `${title} ${titleAndSiteTitleSeparator} ${titlePart}`;
    } else {
      return seo_title || title;
    }
  };

  return (
    <>
      <Helmet>
        <title>{getTitle()?.replace(/\u00AD/g, '')}</title>
        <link
          rel="canonical"
          href={seo_canonical_url || toPublicURL(props.content['@id'])}
        />
        <meta name="description" content={seo_description || description} />
        <meta
          property="og:title"
          content={opengraph_title || seo_title || title}
        />
        <meta
          property="og:url"
          content={seo_canonical_url || toPublicURL(props.content['@id'])}
        />
        {seo_noindex && <meta name="robots" content="noindex" />}
        {contentImageInfo.contentHasImage && (
          <meta
            property="og:image"
            content={toPublicURL(contentImageInfo.url)}
          />
        )}
        {contentImageInfo.contentHasImage && (
          <meta property="og:image:width" content={contentImageInfo.width} />
        )}
        {contentImageInfo.contentHasImage && (
          <meta property="og:image:height" content={contentImageInfo.height} />
        )}
        {(opengraph_description || seo_description || description) && (
          <meta
            property="og:description"
            content={opengraph_description || seo_description || description}
          />
        )}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
    </>
  );
};

export default ContentMetadataTags;
